import React from 'react';
import { Send, Mail, MapPin, MessageCircle, Clock } from 'lucide-react';
import { PRODUCTS } from '../data/mock';
import { useCart } from '../context/CartContext';
import ProductCard from '../components/ProductCard';
import { Heart } from 'lucide-react';
import { Link } from 'react-router-dom';
import Seo, { SITE_URL } from '../components/Seo';

export const About = () => (
  <div className="max-w-5xl mx-auto px-4 py-16">
    <Seo title="O nas | AlphaPro Genetics" description="Poznaj AlphaPro Genetics oraz najważniejsze informacje o sklepie." path="/o-nas" structuredData={{ '@context': 'https://schema.org', '@type': 'WebPage', name: 'O nas | AlphaPro Genetics', url: `${SITE_URL}/o-nas`, inLanguage: 'pl-PL' }} />
    <div className="bg-gradient-to-r from-blue-50 to-sky-50 rounded-3xl p-10 mb-10 border border-blue-100">
      <span className="text-blue-600 font-semibold text-sm uppercase tracking-widest">O nas</span>
      <h1 className="text-4xl font-bold text-slate-900 mt-2 tracking-tight">Twoje zaufane źródło sterydy i peptydów</h1>
    </div>
    <div className="prose prose-slate max-w-none">
      <p className="text-lg text-slate-700 leading-relaxed">
        AlphaPro Genetics to polski sklep działający od 2021 roku, specjalizujący się w dostarczaniu produktów farmaceutycznych klasy premium - sterydów anabolicznych, peptydów, hormonów wzrostu oraz suplementów PCT.
      </p>
      <p className="text-slate-700 leading-relaxed mt-4">
        Współpracujemy bezpośrednio ze sprawdzonymi laboratoriami, dzięki czemu możemy zagwarantować oryginalność i czystość każdej serii. Nasze produkty są przechowywane w optymalnych warunkach, a wysyłka odbywa się maksymalnie 48h po zaksięgowaniu płatności.
      </p>
      <p className="text-slate-700 leading-relaxed mt-4">
        Naszym priorytetem jest pełna anonimowość klientów - kontakt odbywa się wyłącznie przez komunikator Telegram, a strona nie zbiera żadnych danych osobowych. To gwarancja bezpieczeństwa i prywatności, której oczekujesz.
      </p>
      <div className="grid sm:grid-cols-3 gap-4 mt-10">
        <div className="bg-white border border-blue-100 rounded-2xl p-6 text-center">
          <div className="text-4xl font-bold text-blue-600">15K+</div>
          <div className="text-sm text-slate-600 mt-1">Klientów</div>
        </div>
        <div className="bg-white border border-blue-100 rounded-2xl p-6 text-center">
          <div className="text-4xl font-bold text-blue-600">2021</div>
          <div className="text-sm text-slate-600 mt-1">Od kiedy działamy</div>
        </div>
        <div className="bg-white border border-blue-100 rounded-2xl p-6 text-center">
          <div className="text-4xl font-bold text-blue-600">48h</div>
          <div className="text-sm text-slate-600 mt-1">Czas wysyłki</div>
        </div>
      </div>
    </div>
  </div>
);

export const Contact = () => (
  <div className="max-w-5xl mx-auto px-4 py-16">
    <Seo title="Kontakt | AlphaPro Genetics" description="Skontaktuj się z AlphaPro Genetics w sprawie produktów i zamówień." path="/kontakt" structuredData={{ '@context': 'https://schema.org', '@type': 'WebPage', name: 'Kontakt | AlphaPro Genetics', url: `${SITE_URL}/kontakt`, inLanguage: 'pl-PL' }} />
    <div className="text-center mb-12">
      <span className="text-blue-600 font-semibold text-sm uppercase tracking-widest">Kontakt</span>
      <h1 className="text-4xl font-bold text-slate-900 mt-2 tracking-tight">Skontaktuj się z nami</h1>
      <p className="text-slate-600 mt-3 max-w-xl mx-auto">Wszystkie zapytania dotyczące zamówień obsługujemy przez Telegram - dla pełnej anonimowości.</p>
    </div>

    <div className="grid md:grid-cols-2 gap-6">
      <a href="https://t.me/+9A5CpRihCfNjMzc0" target="_blank" rel="noreferrer" className="group bg-gradient-to-br from-blue-600 to-sky-500 rounded-3xl p-8 text-white hover:shadow-2xl hover:shadow-blue-200 transition">
        <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center mb-4 group-hover:scale-110 transition">
          <Send className="w-7 h-7" />
        </div>
        <h3 className="text-2xl font-bold mb-2">Telegram</h3>
        <p className="text-blue-100 mb-4">Główny kanał kontaktu - szybka odpowiedź i pełna dyskrecja.</p>
        <div className="inline-flex items-center gap-2 bg-white text-blue-600 px-4 py-2 rounded-full font-semibold text-sm">Otwórz Telegram</div>
      </a>

      <div className="bg-white border border-blue-100 rounded-3xl p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-5">Informacje kontaktowe</h3>
        <ul className="space-y-4">
          <li className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0"><Mail className="w-5 h-5 text-blue-600" /></div>
            <div>
              <div className="font-semibold text-slate-900 text-sm">Email</div>
              <div className="text-sm text-slate-600">kontakt@alphapro-genetics.pl</div>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0"><MapPin className="w-5 h-5 text-blue-600" /></div>
            <div>
              <div className="font-semibold text-slate-900 text-sm">Lokalizacja</div>
              <div className="text-sm text-slate-600">Warszawa, Polska</div>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0"><Clock className="w-5 h-5 text-blue-600" /></div>
            <div>
              <div className="font-semibold text-slate-900 text-sm">Godziny pracy</div>
              <div className="text-sm text-slate-600">24/7 przez Telegram</div>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0"><MessageCircle className="w-5 h-5 text-blue-600" /></div>
            <div>
              <div className="font-semibold text-slate-900 text-sm">Czas odpowiedzi</div>
              <div className="text-sm text-slate-600">Zwykle do 30 minut</div>
            </div>
          </li>
        </ul>
      </div>
    </div>
  </div>
);

export const Wishlist = () => {
  const { wishlist } = useCart();
  const items = PRODUCTS.filter(p => wishlist.includes(p.id));

  return (
    <div className="max-w-7xl mx-auto px-4 py-10">
      <Seo title="Ulubione produkty | AlphaPro Genetics" description="Lista ulubionych produktów AlphaPro Genetics." path="/ulubione" noIndex />
      <div className="flex items-center gap-3 mb-8">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-600 to-sky-400 flex items-center justify-center shadow-lg shadow-blue-200">
          <Heart className="w-6 h-6 text-white" />
        </div>
        <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Ulubione produkty</h1>
      </div>
      {items.length === 0 ? (
        <div className="bg-white rounded-3xl border border-blue-100 p-16 text-center">
          <div className="w-20 h-20 rounded-full bg-blue-50 mx-auto flex items-center justify-center mb-4">
            <Heart className="w-8 h-8 text-blue-300" />
          </div>
          <p className="text-slate-600 mb-5">Brak produktów w ulubionych</p>
          <Link to="/sklep" className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-full font-semibold text-sm transition">Przejdź do sklepu</Link>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          {items.map(p => <ProductCard key={p.id} product={p} />)}
        </div>
      )}
    </div>
  );
};
